import React, { useState, useEffect, useRef } from 'react';
import AdminHeader from "../Header/AdminHeader";
import AdminFooter from "../Footer/AdminFooter";
import AdminLeftMenu from "../Navbar/AdminLeftMenu";
import Select from "react-select";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCalendarAlt, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { userloginDetails } from '../Utils/AdminAuth';
import { NODE_HOST_URL_v1 } from '../Dashboard/api/Api';
import Axios from '../../axios/Axios';

const toInputDate = (d) => d.toISOString().split('T')[0];

const JsonBlock = ({ value }) => {
    let formatted = '';
    try {
        const obj = typeof value === 'string' ? JSON.parse(value) : value;
        formatted = JSON.stringify(obj, null, 2);
    } catch {
        formatted = value || '';
    }

    const tokens = [];
    let remaining = formatted;
    const regex = /("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?|[{}[\],:]|\n| +)/g;
    let match;
    while ((match = regex.exec(formatted)) !== null) {
        const token = match[0];
        let color = '#d4d4d4';
        if (token === 'true' || token === 'false') color = '#569cd6';
        else if (token === 'null') color = '#569cd6';
        else if (/^-?\d/.test(token)) color = '#b5cea8';
        else if (/^"/.test(token)) color = /:$/.test(token) ? '#9cdcfe' : '#ce9178';
        tokens.push({ token, color });
    }

    return (
        <div style={{
            background: '#1e1e2e', borderRadius: '8px',
            padding: '10px 12px',
            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            fontSize: '11px', lineHeight: '1.6',
            maxHeight: '160px', overflowY: 'auto',
            minWidth: '200px', maxWidth: '340px',
            whiteSpace: 'pre',
        }}>
            {tokens.map((t, i) => (
                <span key={i} style={{ color: t.color }}>{t.token}</span>
            ))}
        </div>
    );
};

const today = new Date();
const sixDaysAgo = new Date();
sixDaysAgo.setDate(today.getDate() - 5);

const ApiFailedRequest = () => {
    const lStorage = userloginDetails();
    const userId = lStorage?.user?.userId;
    const userToken = lStorage?.token;
    const adminType = lStorage?.user?.isAdmin;

    const [selectedUser, setSelectedUser] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const debounceRef = useRef(null);
    const [fromDate, setFromDate] = useState(toInputDate(today));
    const [toDate, setToDate] = useState(toInputDate(today));
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [error, setError] = useState({ status: false, bgColor: '', message: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const customStyles = {
        control: (provided) => ({ ...provided, cursor: 'pointer' }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (style) => ({ ...style, fontSize: '12px' }),
    };

    const searchUsers = async (value) => {
        setIsLoadingUsers(true);
        try {
            const { data } = await Axios.post(`https://napi.authkey.io/api2/v1/admin/users/search`, {
                user_id: userId,
                token: userToken,
                method: 'search_user',
                search_value: value,
            });
            if (data.success) {
                setAllUsers(data.data.map(u => ({ value: u.id, label: u.email })));
            } else {
                setAllUsers([]);
            }
        } catch (err) {
            console.error('Error searching users:', err);
            setAllUsers([]);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const handleInputChange = (value) => {
        setInputValue(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (value.length >= 4) {
            debounceRef.current = setTimeout(() => searchUsers(value), 400);
        } else {
            setAllUsers([]);
        }
    };

    const showError = (msg, type = 'danger') => {
        setError({ status: true, bgColor: `alert alert-${type}`, message: msg });
        setTimeout(() => setError({ status: false, bgColor: '', message: '' }), 3000);
    };

    const handleSearch = async (page = 1) => {
        if (!selectedUser) return showError('Please select a user.');
        if (!fromDate || !toDate) return showError('Please select both dates.');
        if (new Date(fromDate) > new Date(toDate)) return showError('From date cannot be after To date.');

        setLoading(true);
        setSearched(true);
        setTableData([]);
        try {
            const { data } = await Axios.post(`${NODE_HOST_URL_v1}/admin/failedRequests`, {
                user_id: userId,
                method: "getFailedRequests",
                user_type: adminType,
                token: userToken,
                retr_user_id: String(selectedUser.value),
                from_date: fromDate,
                to_date: toDate,
                page,
            });
            if (data.success) {
                setTableData(data.failed_requests || []);
                setCurrentPage(data.pagination?.current_page || page);
                setTotalPages(data.pagination?.total_pages || 1);
                setTotalRecords(data.pagination?.total_records || 0);
            } else {
                showError(data.message);
            }
        } catch (err) {
            showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        handleSearch(page);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
            .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
            }, []);

        return (
            <div style={{
                padding: '14px 24px', borderTop: '1px solid #f0f0f8',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px',
            }}>
                <span style={{ fontSize: '13px', color: '#334155', fontWeight: '600' }}>
                    Page <span style={{ color: '#4f46e5' }}>{currentPage}</span> of{' '}
                    <span style={{ color: '#4f46e5' }}>{totalPages}</span>
                    {totalRecords > 0 && (
                        <span style={{ color: '#64748b', fontWeight: '400' }}>
                            {' '}({totalRecords.toLocaleString()} records)
                        </span>
                    )}
                </span>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{
                            height: '32px', padding: '0 12px', borderRadius: '8px',
                            border: '1px solid #e2e8f0', fontSize: '12px', fontWeight: '600',
                            background: currentPage === 1 ? '#f8fafc' : '#fff',
                            color: currentPage === 1 ? '#cbd5e1' : '#4f46e5',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: '4px',
                        }}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '10px' }} /> Prev
                    </button>

                    {pages.map((p, idx) =>
                        p === '...' ? (
                            <span key={'e' + idx} style={{ padding: '0 4px', color: '#94a3b8', fontSize: '13px' }}>…</span>
                        ) : (
                            <button
                                key={p}
                                onClick={() => handlePageChange(p)}
                                style={{
                                    minWidth: '32px', height: '32px', borderRadius: '8px', fontSize: '12px',
                                    border: '1px solid', fontWeight: currentPage === p ? '700' : '400',
                                    borderColor: currentPage === p ? '#4f46e5' : '#e2e8f0',
                                    background: currentPage === p ? '#4f46e5' : '#fff',
                                    color: currentPage === p ? '#fff' : '#475569',
                                    cursor: 'pointer',
                                }}
                            >
                                {p}
                            </button>
                        )
                    )}

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        style={{
                            height: '32px', padding: '0 12px', borderRadius: '8px',
                            border: '1px solid #e2e8f0', fontSize: '12px', fontWeight: '600',
                            background: currentPage >= totalPages ? '#f8fafc' : '#fff',
                            color: currentPage >= totalPages ? '#cbd5e1' : '#4f46e5',
                            cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: '4px',
                        }}
                    >
                        Next <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '10px' }} />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="wrapper">
            <AdminLeftMenu />
            <div className="main">
                <AdminHeader />
                <main className="content">

                    {/* Filter Card */}
                    <div className="card">
                        <div className="card-body">
                            <h5 className="card-title mb-3">Request Limit</h5>
                            <hr />

                            {error.status && (
                                <div className={error.bgColor} role="alert">
                                    <button
                                        type="button"
                                        className="close"
                                        onClick={() => setError({ status: false })}
                                        aria-label="Close"
                                    >
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                    {error.message}
                                </div>
                            )}

                            <div className="row g-2 align-items-end">
                                <div className="col-12 col-md-4">
                                    <label className="mb-1">
                                        Select User <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <Select
                                        value={selectedUser}
                                        onChange={setSelectedUser}
                                        options={allUsers}
                                        inputValue={inputValue}
                                        onInputChange={handleInputChange}
                                        placeholder="Type at least 4 characters..."
                                        styles={customStyles}
                                        isSearchable
                                        isLoading={isLoadingUsers}
                                        filterOption={() => true}
                                        noOptionsMessage={() =>
                                            inputValue.length < 4
                                                ? 'Type at least 4 characters to search'
                                                : isLoadingUsers
                                                ? 'Searching...'
                                                : 'No record match'
                                        }
                                    />
                                </div>

                                <div className="col-12 col-sm-6 col-md-3">
                                    <label className="mb-1">From Date</label>
                                    <div className="input-group">
                                        <div className="input-group-text">
                                            <FontAwesomeIcon icon={faCalendarAlt} />
                                        </div>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={fromDate}
                                            min={toInputDate(sixDaysAgo)}
                                            max={toDate || toInputDate(today)}
                                            onChange={(e) => setFromDate(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-sm-6 col-md-3">
                                    <label className="mb-1">To Date</label>
                                    <div className="input-group">
                                        <div className="input-group-text">
                                            <FontAwesomeIcon icon={faCalendarAlt} />
                                        </div>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={toDate}
                                            min={fromDate || toInputDate(sixDaysAgo)}
                                            max={toInputDate(today)}
                                            onChange={(e) => setToDate(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-2">
                                    <button
                                        className="btn btn-success w-100"
                                        onClick={() => handleSearch(1)}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <span className="spinner-border spinner-border-sm" />
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faSearch} className="mr-1" />
                                                Search
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* List Card */}
                    <div style={{
                        background: '#fff', borderRadius: '12px',
                        boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
                        border: '1px solid #e8e8f0',
                        marginTop: '20px', overflow: 'hidden',
                        fontFamily: 'Inter, system-ui, sans-serif',
                    }}>
                        <div style={{ padding: '18px 24px', borderBottom: '1px solid #f0f0f8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1a1a2e' }}>
                                Latest Failed Request
                            </h5>
                            {totalRecords > 0 && (
                                <span style={{
                                    fontSize: '12px', fontWeight: '600',
                                    background: '#eef2ff', color: '#4f46e5',
                                    border: '1px solid #c7d2fe',
                                    borderRadius: '20px', padding: '3px 12px',
                                }}>
                                    {totalRecords.toLocaleString()} Records
                                </span>
                            )}
                        </div>

                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
                                <span className="spinner-border spinner-border-sm mr-2" />
                                {' '}Loading...
                            </div>
                        ) : tableData.length > 0 ? (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                                    <thead>
                                        <tr style={{ background: '#2d2d4e' }}>
                                            {['API Request', 'Response', 'Other Details'].map((h, i) => (
                                                <th key={i} style={{
                                                    padding: '14px 18px', fontSize: '12px', fontWeight: '700',
                                                    color: '#e2e2f0', textAlign: 'left', whiteSpace: 'nowrap',
                                                    letterSpacing: '0.3px',
                                                }}>
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableData.map((row, i) => (
                                            <tr key={i} style={{ background: i % 2 === 0 ? '#f4f4fb' : '#fff', borderBottom: '1px solid #ebebf5' }}>
                                                <td style={{ padding: '12px 18px' }}>
                                                    <JsonBlock value={row.request_str} />
                                                </td>
                                                <td style={{ padding: '16px 18px', fontSize: '12px', color: '#475569', whiteSpace: 'nowrap' }}>
                                                    {row.reason ?? '-'}
                                                </td>
                                                <td style={{ padding: '14px 18px' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                        {row.logid && (
                                                            <span style={{ fontSize: '12px', color: '#475569' }}>
                                                                <span style={{ fontWeight: '600', color: '#64748b' }}><b>Log ID:</b> </span>{row.logid}
                                                            </span>
                                                        )}
                                                        {row.ip && (
                                                            <span style={{ fontSize: '12px', color: '#475569' }}>
                                                                <span style={{ fontWeight: '600', color: '#64748b' }}><b>IP:</b> </span>{row.ip}
                                                            </span>
                                                        )}
                                                        {row.created && (
                                                            <span style={{ fontSize: '12px', color: '#475569' }}>
                                                                <span style={{ fontWeight: '600', color: '#64748b' }}><b>Date:</b> </span>{row.created}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            {renderPagination()}
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: '14px' }}>
                                {searched ? 'No records found.' : 'Search to see results.'}
                            </div>
                        )}
                    </div>

                </main>
                <AdminFooter />
            </div>
        </div>
    );
};

export default ApiFailedRequest;
